import type { ReactElement } from 'react';

import { Tooltip } from 'components/ui/tooltip';
import { useEnvIndicator } from 'layout/MainLayout/Header/EnvIndicatorBar';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

const TONE = {
    success: 'bg-env-dev text-primary-ink',
    warning: 'bg-env-test text-warning-ink',
};

const useEnv = () => {
    const env = useEnvIndicator();
    return (
        env && {
            ...env,
            // Without the tenant the label names the environment alone, rather than a tenant still "loading".
            label: env.tenantName
                ? t('portal.environment-indicator', { environment: env.environment, tenant: env.tenantName })
                : t('portal.environment-only', { environment: env.environment }),
        }
    );
};

/**
 * The new design's stand-in for classic's environment ribbon, shown in the same environments: a pill with the
 * environment's short name, and the environment and tenant the ribbon names as its tooltip and accessible name.
 */
export function EnvChip() {
    const env = useEnv();
    if (!env) {
        return null;
    }

    return (
        <Tooltip content={env.label} side="bottom">
            <span
                className={cn(
                    'text-caption flex h-[18px] shrink-0 items-center rounded-full px-1.5 leading-none font-semibold tracking-[0.04em]',
                    TONE[env.tone]
                )}
            >
                <span aria-hidden>{env.short}</span>
                <span className="sr-only">{env.label}</span>
            </span>
        </Tooltip>
    );
}

/**
 * On the rail: `children`, the monogram button, under a tooltip that names it with `label`. In an environment that
 * has a ribbon, its short name sits in a pill under the button, so the name and not the colour tells the
 * environments apart, and the chip's label is the tooltip's second line.
 */
export function EnvMarker({ label, children }: { label: string; children: ReactElement }) {
    const env = useEnv();
    if (!env) {
        return (
            <Tooltip content={label} side="right">
                {children}
            </Tooltip>
        );
    }

    return (
        <span className="relative flex">
            <Tooltip content={`${label}\n${env.label}`} side="right">
                {children}
            </Tooltip>
            <span
                aria-hidden
                className={cn(
                    'ring-sidebar text-caption pointer-events-none absolute -bottom-2 left-1/2 flex h-3.5 -translate-x-1/2 items-center rounded-full px-1 leading-none font-semibold tracking-[0.04em] ring-2',
                    TONE[env.tone]
                )}
            >
                {env.short}
            </span>
            <span className="sr-only">{env.label}</span>
        </span>
    );
}
